import {
  MeetingStatusSchema,
  OutcomeSchema,
  PlatformSchema,
  SentimentLevelSchema,
  type MeetingStatus,
  type Outcome,
  type Platform,
  type SentimentLevel,
} from '@copilot/shared';
import { dayEndIso, dayStartIso, parseDay } from './dates';

/**
 * Meetings-list filters as kept in the URL query string. Dates are local
 * calendar days ("YYYY-MM-DD"); they are converted to ISO timestamps only when
 * calling the API (see `meetingFiltersToApiQuery`).
 */
export type MeetingSortKey = 'startedAt' | 'durationSec' | 'title' | 'client' | 'rep';
export const MEETING_SORT_KEYS: readonly MeetingSortKey[] = ['startedAt', 'durationSec', 'title', 'client', 'rep'];

export interface MeetingFilters {
  q?: string;
  repId?: string;
  teamId?: string;
  clientId?: string;
  platform?: Platform;
  outcome?: Outcome;
  status?: MeetingStatus;
  sentiment?: SentimentLevel;
  from?: string;
  to?: string;
  sort: MeetingSortKey;
  order: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export const DEFAULT_MEETING_FILTERS: MeetingFilters = { sort: 'startedAt', order: 'desc', page: 1, pageSize: 25 };
export const PAGE_SIZES = [10, 25, 50, 100] as const;

function oneOf<T extends string>(options: readonly T[], value: string | null): T | undefined {
  return value != null && (options as readonly string[]).includes(value) ? (value as T) : undefined;
}

function nonEmpty(value: string | null): string | undefined {
  const v = value?.trim();
  return v ? v : undefined;
}

function positiveInt(value: string | null, fallback: number, max = Number.MAX_SAFE_INTEGER): number {
  const n = value == null ? NaN : Number(value);
  return Number.isInteger(n) && n >= 1 ? Math.min(n, max) : fallback;
}

/** URL -> filters. Unknown or invalid values are dropped (never throws). */
export function parseMeetingFilters(sp: URLSearchParams): MeetingFilters {
  const from = nonEmpty(sp.get('from'));
  const to = nonEmpty(sp.get('to'));
  return {
    q: nonEmpty(sp.get('q')),
    repId: nonEmpty(sp.get('repId')),
    teamId: nonEmpty(sp.get('teamId')),
    clientId: nonEmpty(sp.get('clientId')),
    platform: oneOf(PlatformSchema.options, sp.get('platform')),
    outcome: oneOf(OutcomeSchema.options, sp.get('outcome')),
    status: oneOf(MeetingStatusSchema.options, sp.get('status')),
    sentiment: oneOf(SentimentLevelSchema.options, sp.get('sentiment')),
    from: parseDay(from) ? from : undefined,
    to: parseDay(to) ? to : undefined,
    sort: oneOf(MEETING_SORT_KEYS, sp.get('sort')) ?? DEFAULT_MEETING_FILTERS.sort,
    order: oneOf(['asc', 'desc'] as const, sp.get('order')) ?? DEFAULT_MEETING_FILTERS.order,
    page: positiveInt(sp.get('page'), 1),
    pageSize: positiveInt(sp.get('pageSize'), DEFAULT_MEETING_FILTERS.pageSize, 100),
  };
}

const FILTER_KEYS = ['q', 'repId', 'teamId', 'clientId', 'platform', 'outcome', 'status', 'sentiment', 'from', 'to'] as const;

/** Filters -> URL. Empty values and defaults are omitted to keep URLs short. */
export function meetingFiltersToSearchParams(f: MeetingFilters): URLSearchParams {
  const sp = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const v = f[key];
    if (v) sp.set(key, v);
  }
  if (f.sort !== DEFAULT_MEETING_FILTERS.sort) sp.set('sort', f.sort);
  if (f.order !== DEFAULT_MEETING_FILTERS.order) sp.set('order', f.order);
  if (f.page !== 1) sp.set('page', String(f.page));
  if (f.pageSize !== DEFAULT_MEETING_FILTERS.pageSize) sp.set('pageSize', String(f.pageSize));
  return sp;
}

/**
 * Apply a change to the filters. Any change other than page resets to page 1.
 */
export function updateMeetingFilters(f: MeetingFilters, patch: Partial<MeetingFilters>): MeetingFilters {
  const next = { ...f, ...patch };
  if (!('page' in patch)) next.page = 1;
  return next;
}

/** Clicking a column header: same column flips order; a new column starts desc for dates/durations, asc for text. */
export function toggleSort(f: MeetingFilters, key: MeetingSortKey): MeetingFilters {
  if (f.sort === key) return updateMeetingFilters(f, { order: f.order === 'asc' ? 'desc' : 'asc' });
  const numeric = key === 'startedAt' || key === 'durationSec';
  return updateMeetingFilters(f, { sort: key, order: numeric ? 'desc' : 'asc' });
}

export function hasActiveFilters(f: MeetingFilters): boolean {
  return FILTER_KEYS.some((k) => !!f[k]);
}

export function clearMeetingFilters(f: MeetingFilters): MeetingFilters {
  return { ...DEFAULT_MEETING_FILTERS, sort: f.sort, order: f.order, pageSize: f.pageSize };
}

/**
 * Filters -> query parameters for GET /v1/meetings (and export.csv).
 * Day filters become ISO timestamps covering whole local days.
 */
export function meetingFiltersToApiQuery(
  f: MeetingFilters,
  opts: { scope?: 'visible' | 'mine'; includePaging?: boolean } = {},
): Record<string, string> {
  const q: Record<string, string> = {};
  for (const key of FILTER_KEYS) {
    const v = f[key];
    if (!v) continue;
    if (key === 'from') {
      const iso = dayStartIso(v);
      if (iso) q.from = iso;
    } else if (key === 'to') {
      const iso = dayEndIso(v);
      if (iso) q.to = iso;
    } else {
      q[key] = v;
    }
  }
  q.sort = f.sort;
  q.order = f.order;
  if (opts.includePaging !== false) {
    q.page = String(f.page);
    q.pageSize = String(f.pageSize);
  }
  if (opts.scope && opts.scope !== 'visible') q.scope = opts.scope;
  return q;
}

/** Build "?a=1&b=2" (or "") from a record, skipping empty values. */
export function toQueryString(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}
